import { useState } from 'react';
import { Building2, Plus } from 'lucide-react';
import { P } from '@stockpilot/shared';
import {
  useBranchesQuery, useCreateBranchMutation, useDeleteBranchMutation, type Branch,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import {
  Alert, Badge, Button, Card, Checkbox, ConfirmDialog, EmptyState, ErrorState, Input,
  LoadingState, Modal, PageHeader, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';

export function BranchesPage() {
  const { data, isLoading, isError, error, refetch } = useBranchesQuery();
  const [createBranch, createState] = useCreateBranchMutation();
  const [deleteBranch, deleteState] = useDeleteBranchMutation();

  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Branch | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', code: '', city: '', phone: '', isDefault: false });

  const fieldErrors = apiFieldErrors(createState.error);

  const submit = async (): Promise<void> => {
    const created = await createBranch({
      name: form.name,
      code: form.code,
      city: form.city || undefined,
      phone: form.phone || undefined,
      isDefault: form.isDefault,
    })
      .unwrap()
      .catch(() => null);

    if (created) {
      setCreating(false);
      setForm({ name: '', code: '', city: '', phone: '', isDefault: false });
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      await deleteBranch(pendingDelete.id).unwrap();
      setPendingDelete(null);
    } catch (deletionError) {
      setDeleteError(apiErrorMessage(deletionError, 'This branch could not be closed'));
    }
  };

  return (
    <>
      <PageHeader
        title="Branches"
        description="Physical locations you trade from. Members can be scoped to specific branches."
        action={
          <Can permission={P.BRANCH_CREATE}>
            <Button icon={Plus} onClick={() => setCreating(true)}>New branch</Button>
          </Can>
        }
      />

      <Card>
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState icon={Building2} title="No branches" description="Create your first branch to start trading." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Branch</Th>
                <Th>Location</Th>
                <Th>Contact</Th>
                <Th numeric>Warehouses</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {data?.items.map((branch) => (
                <tr key={branch.id} className="hover:bg-sunken">
                  <Td>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{branch.name}</p>
                      {branch.isDefault && <Badge tone="brand">Default</Badge>}
                    </div>
                    <p className="text-xs text-muted">{branch.code}</p>
                  </Td>
                  <Td className="text-secondary">{branch.city ?? '—'}</Td>
                  <Td className="text-secondary">{branch.phone ?? branch.email ?? '—'}</Td>
                  <Td numeric className="text-secondary">{branch._count?.warehouses ?? 0}</Td>
                  <Td>
                    <Badge tone={branch.status === 'ACTIVE' ? 'success' : 'neutral'}>
                      {branch.status.toLowerCase()}
                    </Badge>
                  </Td>
                  <Td className="text-right">
                    <Can permission={P.BRANCH_DELETE}>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={branch.isDefault}
                        onClick={() => {
                          setDeleteError(null);
                          setPendingDelete(branch);
                        }}
                        title={branch.isDefault ? 'Make another branch the default first' : undefined}
                      >
                        Close
                      </Button>
                    </Can>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New branch"
        footer={
          <>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={() => void submit()} loading={createState.isLoading} disabled={!form.name || !form.code}>
              Create branch
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {createState.error && <Alert tone="danger">{apiErrorMessage(createState.error)}</Alert>}
          <Input
            label="Name"
            required
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            error={fieldErrors.name}
          />
          <Input
            label="Code"
            required
            placeholder="KLA"
            value={form.code}
            onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })}
            error={fieldErrors.code}
          />
          <Input label="City" value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} />
          <Input label="Phone" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
          <Checkbox
            label="Make this the default branch"
            checked={form.isDefault}
            onChange={(event) => setForm({ ...form, isDefault: event.target.checked })}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
        title="Close this branch?"
        message={
          deleteError ??
          `${pendingDelete?.name} will be marked closed. Its sales history stays intact and readable.`
        }
        confirmLabel="Close branch"
        loading={deleteState.isLoading}
      />
    </>
  );
}
